/**
 * What a catalog item is. A service is sold like a product (same list,
 * basket, checkout and receipt) but keeps no stock: no quantity, minimum
 * level, wastage or transfers. Items saved before services existed carry no
 * kind and are products. One home for that rule, shared by the till, the
 * basket and the back office.
 */

export type ProductKind = 'product' | 'service';

/** True for a stored or typed kind value ("product" | "service"). */
export const isProductKind = (value: unknown): value is ProductKind =>
  value === 'product' || value === 'service';

/** Unit a per-hour service is priced and sold in ("Ksh 800/hr", "1.5 hr"). */
export const SERVICE_HOUR_UNIT = 'hr';

/** How a service is charged: a fixed price per service, or a rate per hour. */
export type ServiceChargeType = 'per_service' | 'per_hour';

export const SERVICE_CHARGE_OPTIONS: Array<{
  value: ServiceChargeType;
  label: string;
  hint: string;
}> = [
  {
    value: 'per_service',
    label: 'Per service',
    hint: 'a fixed price, like 1 haircut',
  },
  {
    value: 'per_hour',
    label: 'Per hour',
    hint: 'a rate, hours entered at the till',
  },
];

/** True for a stored charge type value ("per_service" | "per_hour"). */
export const isServiceChargeType = (
  value: unknown,
): value is ServiceChargeType =>
  value === 'per_service' || value === 'per_hour';

/** Shown where a product shows its stock: the service keeps none. */
export const SERVICE_STOCK_LABEL = 'Not tracked';
export const SERVICE_STOCK_NOTE =
  'No stock count, minimum level, wastage or transfers for this item.';

type KindCarrier = { bp_product_kind?: string | null } | null | undefined;

/** The kind of a product or order line; a missing kind is a product. */
export const getProductKind = (product: KindCarrier): ProductKind =>
  product?.bp_product_kind === 'service' ? 'service' : 'product';

export const isServiceProduct = (product: KindCarrier): boolean =>
  getProductKind(product) === 'service';

/** A basket line (CartItem) carries the kind as `kind`. */
export const isServiceLine = (
  line: { kind?: string | null } | null | undefined,
): boolean => line?.kind === 'service';

/**
 * False for a service: there is nothing to run out of, so no stock check,
 * stock badge or "out of stock" state applies.
 */
export const isStockTracked = (
  kind: ProductKind | string | null | undefined,
): boolean => kind !== 'service';

/**
 * How much of an item can be sold: its stock, or no limit for a service. For
 * code that caps with `Math.min` or compares against one number instead of
 * gating each check on `isStockTracked`.
 */
export const getSellLimit = (
  kind: ProductKind | string | null | undefined,
  stock: number,
): number => (isStockTracked(kind) ? stock : Infinity);

/** A per-hour service is a per-unit item whose unit is the hour. */
export const getServiceChargeType = (
  priceType: string | null | undefined,
): ServiceChargeType => (priceType === 'unit' ? 'per_hour' : 'per_service');

export const getServicePriceType = (
  chargeType: ServiceChargeType,
): 'item' | 'unit' => (chargeType === 'per_hour' ? 'unit' : 'item');

/** Unit of mass a service stores for its charge type. */
export const getServiceUnit = (chargeType: ServiceChargeType): string =>
  chargeType === 'per_hour' ? SERVICE_HOUR_UNIT : '';

/** "Per service" or "Per hour", as the Service cards show it. */
export const describeServiceCharge = (
  priceType: string | null | undefined,
): string =>
  getServiceChargeType(priceType) === 'per_hour' ? 'Per hour' : 'Per service';

/** "Service" or "Hour": what one unit of the service is, for "Sold per". */
export const describeServiceSoldPer = (
  priceType: string | null | undefined,
): string =>
  getServiceChargeType(priceType) === 'per_hour' ? 'Hour' : 'Service';

/**
 * Label over the till's per-unit input: a per-hour service is entered in
 * hours, a per-unit product is weighed.
 */
export const describeUnitInputLabel = (
  kind: ProductKind | string | null | undefined,
): string => (isStockTracked(kind) ? 'Enter Weight' : 'Enter Hours');

/**
 * The category line with the quiet service marker: "Haircuts · Service", or
 * just "Service" for a service with no category. Products keep their
 * category alone.
 */
export const appendServiceMeta = (
  category: string | null | undefined,
  kind: ProductKind | string | null | undefined,
): string => {
  const base = String(category ?? '').trim();
  if (isStockTracked(kind)) return base;
  return base ? `${base} · Service` : 'Service';
};
